// ---------------------------------------------------------------------------
// PDF exports for the Academy presentation.
// Two versions:
//   1. Training copy  — one-for-one slides + the full assessment question bank
//   2. Investor deck  — the same slides & screenshots reframed as a product pitch
// Implementation: a fully-styled print document opens in a new tab and the
// browser's print dialog is triggered — the user picks "Save as PDF".
// ---------------------------------------------------------------------------

import {
  PRESENTATION_SLIDES,
  REVIEW_BIG_IDEAS,
  COURSE_META,
} from '@/data/trainingPresentation';
import { QUIZ_BANK, BANK_SIZE } from '@/data/trainingQuiz';
import { PASSING_SCORE, QUIZ_SIZE } from '@/data/trainingTypes';
import { COURSE_SECTIONS } from '@/data/trainingAcademy';

const GOLD = '#C9A961';
const NAVY = '#0d1526';

const baseStyles = `
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; color: #1a1f2e; background: #fff; }
  .page { page-break-after: always; padding: 48px 56px; min-height: 96vh; position: relative; }
  .page:last-child { page-break-after: auto; }
  .kicker { font-size: 10px; letter-spacing: 4px; text-transform: uppercase; color: ${GOLD}; font-weight: 700; margin-bottom: 10px; }
  h1 { font-size: 34px; font-weight: 300; letter-spacing: -0.5px; color: ${NAVY}; }
  h2 { font-size: 24px; font-weight: 400; color: ${NAVY}; margin-bottom: 8px; }
  .lead { font-size: 13px; line-height: 1.7; color: #4a5266; margin: 14px 0 20px; max-width: 640px; }
  .points { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 18px; }
  .pt { border: 1px solid #e2e5ec; border-radius: 10px; padding: 12px 14px; }
  .pt b { display: block; font-size: 12px; color: ${NAVY}; margin-bottom: 4px; }
  .pt span { font-size: 11px; line-height: 1.55; color: #5a6275; }
  .tip { background: #faf6ec; border: 1px solid ${GOLD}55; border-radius: 10px; padding: 12px 16px; margin-bottom: 18px; }
  .tip b { font-size: 11px; color: #8a6d2f; text-transform: uppercase; letter-spacing: 2px; }
  .tip p { font-size: 11.5px; color: #5a5340; margin-top: 4px; line-height: 1.6; }
  .shots { display: flex; gap: 18px; align-items: flex-start; }
  .shot-d { flex: 1; border: 1px solid #dfe2ea; border-radius: 10px; overflow: hidden; }
  .shot-d img { width: 100%; display: block; }
  .shot-m { width: 132px; border: 4px solid ${NAVY}; border-radius: 20px; overflow: hidden; flex-shrink: 0; }
  .shot-m img { width: 100%; display: block; }
  .cap { font-size: 9px; color: #9aa1b0; text-transform: uppercase; letter-spacing: 2px; text-align: center; padding: 5px 0; background: #f5f6f9; }
  .footer { position: absolute; bottom: 22px; left: 56px; right: 56px; display: flex; justify-content: space-between; font-size: 9px; color: #9aa1b0; letter-spacing: 1.5px; text-transform: uppercase; border-top: 1px solid #eceef3; padding-top: 8px; }
  .cover { background: ${NAVY}; color: #fff; display: flex; flex-direction: column; justify-content: center; min-height: 97vh; }
  .cover h1 { color: #fff; font-size: 44px; }
  .cover .lead { color: rgba(255,255,255,0.65); }
  .cover .gold { color: ${GOLD}; }
  .badge-row { display: flex; gap: 10px; margin-top: 24px; }
  .badge { border: 1px solid ${GOLD}66; color: ${GOLD}; border-radius: 999px; padding: 6px 16px; font-size: 10px; letter-spacing: 2px; text-transform: uppercase; }
  .q { border: 1px solid #e2e5ec; border-radius: 10px; padding: 12px 16px; margin-bottom: 10px; page-break-inside: avoid; }
  .q .meta { font-size: 9px; letter-spacing: 1.5px; text-transform: uppercase; color: ${GOLD}; margin-bottom: 5px; }
  .q .text { font-size: 12px; color: ${NAVY}; font-weight: 600; margin-bottom: 7px; }
  .q ol { margin-left: 18px; }
  .q li { font-size: 11px; color: #4a5266; line-height: 1.6; }
  .q li.correct { color: #1d7a4f; font-weight: 700; }
  .q .explain { font-size: 10.5px; color: #6a7182; margin-top: 6px; font-style: italic; }
  .recap-list { margin-top: 14px; }
  .recap-item { display: flex; gap: 12px; padding: 8px 0; border-bottom: 1px solid #eef0f5; }
  .recap-item .n { width: 24px; height: 24px; border-radius: 999px; background: ${NAVY}; color: ${GOLD}; font-size: 11px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .recap-item p { font-size: 11.5px; color: #3c4356; line-height: 1.55; }
  .recap-item b { color: ${NAVY}; }
  @page { margin: 0; size: A4; }
`;

function openPrintDocument(title: string, bodyHtml: string): void {
  const w = window.open('', '_blank');
  if (!w) {
    alert('Please allow pop-ups for this site to download the PDF.');
    return;
  }
  w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title><style>${baseStyles}</style></head><body>${bodyHtml}
  <script>
    (function () {
      var imgs = Array.prototype.slice.call(document.images);
      var pending = imgs.filter(function (i) { return !i.complete; }).length;
      function go() { setTimeout(function () { window.focus(); window.print(); }, 400); }
      if (!pending) { go(); return; }
      imgs.forEach(function (img) {
        if (img.complete) return;
        img.addEventListener('load', function () { if (--pending <= 0) go(); });
        img.addEventListener('error', function () { if (--pending <= 0) go(); });
      });
      setTimeout(go, 6000); // safety net
    })();
  <\/script></body></html>`);
  w.document.close();
}

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

function slidePage(s: (typeof PRESENTATION_SLIDES)[number], mode: 'training' | 'pitch', docLabel: string): string {
  const lead = mode === 'pitch' ? s.pitch + ' ' + s.lead : s.lead;
  return `
  <div class="page">
    <div class="kicker">Slide ${s.num} of ${PRESENTATION_SLIDES.length} · ${esc(s.kicker)}</div>
    <h1>${esc(s.title)}</h1>
    <p class="lead">${esc(lead)}</p>
    <div class="points">
      ${s.points.map((p) => `<div class="pt"><b>${esc(p.title)}</b><span>${esc(p.desc)}</span></div>`).join('')}
    </div>
    ${s.tip && mode === 'training' ? `<div class="tip"><b>${esc(s.tip.title)}</b><p>${esc(s.tip.text)}</p></div>` : ''}
    <div class="shots">
      <div class="shot-m"><img src="${s.images.mobile}" alt="" /><div class="cap">iPhone view</div></div>
      <div class="shot-d"><img src="${s.images.desktop}" alt="" /><div class="cap">Desktop view</div></div>
    </div>
    <div class="footer"><span>${docLabel}</span><span>MTN Social Academy</span></div>
  </div>`;
}

function reviewPage(docLabel: string): string {
  return `
  <div class="page">
    <div class="kicker">Slide 16 of 16 · Course review</div>
    <h1>Everything You Just Learned</h1>
    <p class="lead">Fifteen slides covering all ${COURSE_SECTIONS.length} Academy sections — recapped in one place.</p>
    <div class="recap-list">
      ${PRESENTATION_SLIDES.map((s) => `<div class="recap-item"><div class="n">${s.num}</div><p><b>${esc(s.title)}.</b> ${esc(s.recap)}</p></div>`).join('')}
    </div>
    <h2 style="margin-top:22px;">The five big ideas</h2>
    ${REVIEW_BIG_IDEAS.map((b) => `<p style="font-size:11.5px;color:#3c4356;line-height:1.7;">— ${esc(b)}</p>`).join('')}
    <div class="footer"><span>${docLabel}</span><span>MTN Social Academy</span></div>
  </div>`;
}

/** VERSION 1 — one-for-one training copy: all 16 slides + full assessment bank. */
export function downloadTrainingCopyPdf(): void {
  const cover = `
  <div class="page cover">
    <div class="kicker">MTN Social Academy · Official Training Copy</div>
    <h1>${esc(COURSE_META.title)}<br/><span class="gold">Complete Slide &amp; Assessment Booklet</span></h1>
    <p class="lead">A one-for-one copy of the ${PRESENTATION_SLIDES.length}-slide training presentation (plus the review slide), followed by the complete ${BANK_SIZE}-question certification bank with correct answers and explanations. The live assessment draws ${QUIZ_SIZE} random questions; ${PASSING_SCORE}% is required to pass.</p>
    <div class="badge-row">
      <span class="badge">16 slides</span>
      <span class="badge">${BANK_SIZE} questions</span>
      <span class="badge">Pass mark ${PASSING_SCORE}%</span>
    </div>
  </div>`;

  const slides = PRESENTATION_SLIDES.map((s) => slidePage(s, 'training', 'Training copy')).join('');
  const review = reviewPage('Training copy');

  // Group questions by topic for readability
  const byTopic: Record<string, typeof QUIZ_BANK> = {};
  QUIZ_BANK.forEach((q) => {
    (byTopic[q.topicLabel] = byTopic[q.topicLabel] || []).push(q);
  });
  let qNum = 0;
  const quizHtml = Object.entries(byTopic)
    .map(
      ([label, qs]) => `
      <h2 style="margin:18px 0 10px;">${esc(label)}</h2>
      ${qs
        .map((q) => {
          qNum += 1;
          return `
          <div class="q">
            <div class="meta">Question ${qNum} · ${esc(q.difficulty)}</div>
            <div class="text">${esc(q.q)}</div>
            <ol type="A">
              ${q.choices.map((c, i) => `<li class="${i === 0 ? 'correct' : ''}">${esc(c)}${i === 0 ? ' — correct answer' : ''}</li>`).join('')}
            </ol>
            <div class="explain">${esc(q.explain)}</div>
          </div>`;
        })
        .join('')}`,
    )
    .join('');

  const quizIntro = `
  <div class="page">
    <div class="kicker">Certification assessment · Full question bank</div>
    <h1>The Learning Assessment</h1>
    <p class="lead">Every question in the ${BANK_SIZE}-question certification bank, grouped by course topic. In the live assessment, ${QUIZ_SIZE} questions are drawn at random and answer order is shuffled — no two quizzes look the same. The pass mark is ${PASSING_SCORE}%. The correct answer for each question below is marked in green, followed by the official explanation shown to learners after answering.</p>
    ${quizHtml}
    <div style="height:40px"></div>
    <div class="footer"><span>Training copy</span><span>MTN Social Academy</span></div>
  </div>`;

  openPrintDocument('MTN Social Academy — Training Copy', cover + slides + review + quizIntro);
}

/** VERSION 2 — investor / client pitch deck highlighting features & functions. */
export function downloadInvestorDeckPdf(): void {
  const cover = `
  <div class="page cover">
    <div class="kicker">Product Overview · Investor &amp; Client Edition</div>
    <h1>The AI Social Media Hub<br/><span class="gold">One Platform. Every Post. Fully Managed.</span></h1>
    <p class="lead">An AI-powered social media command center that takes content from idea to published post — writing, imagery, approval, scheduling, publishing and analytics — inside a single, brand-safe workspace. The following pages walk through each capability exactly as users experience it, on mobile and desktop.</p>
    <div class="badge-row">
      <span class="badge">AI writing &amp; imagery</span>
      <span class="badge">Approval workflow</span>
      <span class="badge">Auto-scheduling</span>
      <span class="badge">Analytics loop</span>
    </div>
  </div>`;

  const summary = `
  <div class="page">
    <div class="kicker">Executive summary</div>
    <h1>Why It Wins</h1>
    <p class="lead">Social media is a production pipeline. Most teams run it across five disconnected tools with no quality gate. The Hub consolidates the entire pipeline and inserts AI where it multiplies output — never where it risks the brand.</p>
    <div class="points">
      <div class="pt"><b>10x content velocity</b><span>AI drafts captions and generates rights-clean imagery in the saved brand voice — production drops from hours to minutes.</span></div>
      <div class="pt"><b>Brand safety by design</b><span>A mandatory Draft → Pending → Approved gate with a full audit trail makes off-brand publishing structurally impossible.</span></div>
      <div class="pt"><b>Compounding intelligence</b><span>Analytics feed best-posting-time data straight back into Auto-Schedule — the platform improves with every post.</span></div>
      <div class="pt"><b>Built-in enablement</b><span>A ${PRESENTATION_SLIDES.length}-slide interactive academy plus a ${BANK_SIZE}-question certification bank onboard teams and prove competency.</span></div>
    </div>
    <h2 style="margin-top:10px;">The five platform pillars</h2>
    ${REVIEW_BIG_IDEAS.map((b) => `<p style="font-size:11.5px;color:#3c4356;line-height:1.75;">— ${esc(b)}</p>`).join('')}
    <div class="footer"><span>Investor &amp; client edition</span><span>AI Social Media Hub</span></div>
  </div>`;

  const slides = PRESENTATION_SLIDES.map((s) => slidePage(s, 'pitch', 'Investor & client edition')).join('');

  const closing = `
  <div class="page cover">
    <div class="kicker">Next steps</div>
    <h1>From Idea to Feed —<br/><span class="gold">Without the Chaos.</span></h1>
    <p class="lead">Every capability shown in this deck is live in the platform today: AI writing and image generation, the approval workflow, auto-scheduling, direct publishing, analytics, brand management, campaigns, automation, team roles and the certification academy. We would be delighted to walk you through a live demonstration.</p>
    <div class="badge-row">
      <span class="badge">Live product</span>
      <span class="badge">Certified operators</span>
      <span class="badge">Demo on request</span>
    </div>
  </div>`;

  openPrintDocument('AI Social Media Hub — Investor & Client Deck', cover + summary + slides + closing);
}
